import { useState } from 'react';
import { CheckInFeeling } from '../services/checkIn';

const OPTIONS: { feeling: CheckInFeeling; emoji: string; label: string }[] = [
  { feeling: 'rough', emoji: '😫', label: 'Rough' },
  { feeling: 'okay', emoji: '😐', label: 'Okay' },
  { feeling: 'strong', emoji: '💪', label: 'Strong' },
];

interface CheckInProps {
  onSelect: (feeling: CheckInFeeling, note: string | null) => void;
  onSkip: () => void;
}

/**
 * Feel-first check-in: one tap on how today feels (plus an optional note)
 * before the day's plan is generated, so the coach can weigh it.
 */
export function CheckIn({ onSelect, onSkip }: CheckInProps) {
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState<CheckInFeeling | null>(null);

  const select = (feeling: CheckInFeeling) => {
    if (submitted) return;
    setSubmitted(feeling);
    onSelect(feeling, note.trim() || null);
  };

  return (
    <div className="card relative mx-auto max-w-xl overflow-hidden p-6 text-center sm:p-8">
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-indigo-500/15 to-violet-500/15 blur-2xl" />
      <h2 className="text-xl font-bold tracking-tight text-zinc-900 dark:text-white">
        How are you feeling today?
      </h2>
      <p className="mt-1.5 text-sm text-zinc-500 dark:text-zinc-400">
        One tap — your coach weighs this alongside your recovery data.
      </p>

      <div className="mt-6 grid grid-cols-3 gap-3">
        {OPTIONS.map(({ feeling, emoji, label }) => (
          <button
            key={feeling}
            onClick={() => select(feeling)}
            disabled={submitted !== null}
            className={`flex flex-col items-center gap-2 rounded-2xl px-4 py-5 ring-1 transition ${
              submitted === feeling
                ? 'bg-indigo-600/10 ring-indigo-500/40 dark:bg-indigo-400/10'
                : 'bg-zinc-50 ring-zinc-950/5 hover:-translate-y-0.5 hover:bg-zinc-100 dark:bg-zinc-800/60 dark:ring-white/5 dark:hover:bg-zinc-800'
            } ${submitted && submitted !== feeling ? 'opacity-40' : ''}`}
            aria-label={`Feeling ${label.toLowerCase()}`}
          >
            <span className="text-4xl">{emoji}</span>
            <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">{label}</span>
          </button>
        ))}
      </div>

      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={submitted !== null}
        className="input mt-4 w-full"
        placeholder="Add a note (optional)"
        maxLength={200}
        aria-label="Optional check-in note"
      />
      <button
        onClick={onSkip}
        disabled={submitted !== null}
        className="mt-3 text-sm font-medium text-zinc-400 transition hover:text-zinc-600 dark:hover:text-zinc-300"
      >
        Skip — just show my plan
      </button>
    </div>
  );
}
